import React, { useEffect, useState, useCallback } from "react";
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindow } from "@react-google-maps/api";
import SpinningNumber from "./SpinningNumber";
import LoadingSpinner from "./LoadingSpinner";

const containerStyle = {
  width: "100%",
  height: "100%",
};

const CENTER = {
  lat: 43,
  lng: -1,
};

const mapOptions = { maxZoom: 5, minZoom: 2, fullscreenControl: false, mapTypeControl: false, streetViewControl: false };

const libraries = ["visualization"];

const electrecityGradient = [
  "rgba(0, 255, 255, 0)",
  "rgba(0, 255, 255, 0.33)",
  "rgba(0, 255, 255, 0.66)",
  "rgba(0, 255, 255, 1)",
  "rgba(0, 223, 255, 1)",
  "rgba(0, 191, 255, 1)",
  "rgba(0, 159, 255, 1)",
  "rgba(0, 127, 255, 1)",
  "rgba(0, 95, 255, 1)",
  "rgba(0, 63, 255, 1)",
  "rgba(0, 31, 255, 1)",
  "rgba(0, 0, 255, 1)",
  "rgba(0, 0, 239, 1)",
  "rgba(0, 0, 223, 1)",
  "rgba(0, 0, 207, 1)",
  "rgba(0, 0, 191, 1)",
  "rgba(0, 0, 175, 1)",
  "rgba(0, 0, 159, 1)",
  "rgba(0, 0, 143, 1)",
  "rgba(0, 0, 127, 1)",
  "rgba(31, 0, 109, 1)",
  "rgba(63, 0, 91, 1)",
  "rgba(95, 0, 77, 1)",
  "rgba(127, 0, 63, 1)",
  "rgba(159, 0, 47, 1)",
  "rgba(191, 0, 31, 1)",
  "rgba(223, 0, 15, 1)",
  "rgba(255, 0, 0, 1)",
];

const CO2Gradient = [
  "rgba(102, 255, 0, 0)",
  "rgba(102, 255, 0, 0.33)",
  "rgba(102, 255, 0, 0.66)",
  "rgba(102, 255, 0, 1)",
  "rgba(124, 255, 0, 1)",
  "rgba(147, 255, 0, 1)",
  "rgba(170, 255, 0, 1)",
  "rgba(193, 255, 0, 1)",
  "rgba(215, 255, 0, 1)",
  "rgba(238, 255, 0, 1)",
  "rgba(241, 241, 0, 1)",
  "rgba(244, 227, 0, 1)",
  "rgba(246, 213, 0, 1)",
  "rgba(249, 198, 0, 1)",
  "rgba(252, 184, 0, 1)",
  "rgba(255, 170, 0, 1)",
  "rgba(255, 141, 0, 1)",
  "rgba(255, 113, 0, 1)",
  "rgba(255, 85, 0, 1)",
  "rgba(255, 57, 0, 1)",
  "rgba(255, 28, 0, 1)",
  "rgba(255, 0, 0, 1)",
];

console.log("DCP MAP BUILD CANARY 2026-06-25 v3.64 TEST");

console.log("About to request Google Maps with version 3.64");

function Map({ data, settings, totalCO2, totalElectricity, isLoading, maxDensity, fixedMaxDensity, viewOption }) {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyBrq9jZT9KNXAaYtKz-OFCA6_1bMYzZqew",
    libraries,
    version: "3.64",
  });

  console.log(
      "Loaded Google Maps version:",
      window.google?.maps?.version
  );

  console.log(
      "Maps scripts:",
      [...document.querySelectorAll('script[src*="maps.googleapis.com/maps/api/js"]')]
          .map(s => s.src)
  );


  const [LatLngClass, setLatLngClass] = useState(null);

  useEffect(() => {
    if (!isLoaded) return;

    let cancelled = false;

    const loadCoreLibrary = async () => {
      if (typeof window.google?.maps?.LatLng === "function") {
        setLatLngClass(() => window.google.maps.LatLng);
        return;
      }

      const { LatLng } = await window.google.maps.importLibrary("core");

      if (!cancelled) {
        setLatLngClass(() => LatLng);
      }
    };

    loadCoreLibrary().catch(console.error);

    return () => {
      cancelled = true;
    };
  }, [isLoaded]);





  const [map, setMap] = useState(null);
  const [heatMapLayer, setHeatmapLayer] = useState(null);
  const [infoWindow, setInfoWindow] = useState(null);
  const [processedData, setProcessedData] = useState([]);

  useEffect(() => {
    console.log('Map data processing useEffect triggered - data length:', data?.length, 'viewOption:', viewOption, 'settings.type:', settings.type);
    //if (!data) return;
    if (!data || !LatLngClass) return;

    const processData = () => {
      if (!data || !Array.isArray(data) || data.length === 0) {
        console.log('No data available for processing');
        return [];
      }

      const filterTypeKey = settings.type === "carbon" ? 'co2e_emissions' : 'electricity_usage';

      // In cumulative view, data from CryptoEmission.js already has one entry per location
      // with accumulated values. We just need to map it to the heatmap format.
      // In regular view, we may have multiple entries per location (shouldn't happen, but handle it).
      const locationMap = {};

      data.forEach(location => {
        const locationName = location.location_of_servers.location_name;

        // Get the total usage for this location entry
        let totalUsage;
        if (viewOption === "cumulative") {
          // Use the pre-calculated cumulative totals from CryptoEmission.js
          totalUsage = location[filterTypeKey] || 0;
        } else {
          // For regular view, calculate from individual servers
          totalUsage = location.servers_at_location.reduce((acc, server) => acc + server[filterTypeKey], 0);
        }

        if (!locationMap[locationName]) {
          // First time seeing this location
          locationMap[locationName] = {
            //location: new window.google.maps.LatLng(location.location_of_servers.latitude, location.location_of_servers.longitude),
            location: new LatLngClass(
                parseFloat(location.location_of_servers.latitude),
                parseFloat(location.location_of_servers.longitude)
            ),
            weight: totalUsage,  // Set the weight directly
            locationData: location
          };
        } else {
          // Location already exists (shouldn't happen in cumulative view, but handle it)
          console.warn(`[MAP PROCESS] Duplicate location found: ${locationName}, adding weights`);
          locationMap[locationName].weight += totalUsage;
        }
      });

      const result = Object.values(locationMap);
      return result;
    };

    const processed = processData();
    setProcessedData(processed);
  }, [data, settings.type, viewOption, LatLngClass]);

  const calculateRadius = useCallback(() => {
    if (!totalElectricity || isNaN(totalElectricity) || totalElectricity <= 0) {
      return 50; // Default radius if totalElectricity is not available
    }
    
    if (viewOption === "cumulative") {
      return Math.min(totalElectricity / 50, 200); // example calculation, adjust as needed
    }
    return Math.min(totalElectricity/2, 200); // default radius
  }, [totalElectricity, viewOption]);

  useEffect(() => {
    if (!map) return;

    const showHeatmap = () => {
      let hmap = heatMapLayer;

      if (!hmap) {
        hmap = new window.google.maps.visualization.HeatmapLayer({
          data: [],
          map: map,
          radius: calculateRadius(),
        });
        setHeatmapLayer(hmap);
      }

      // Determine the maximum for normalization
      const maxValue = viewOption === "cumulative" && fixedMaxDensity > 0
        ? fixedMaxDensity
        : maxDensity > 0
          ? maxDensity
          : Math.max(...processedData.map(item => item.weight), 1);

      // For cumulative view, apply square root scaling to make early values more visible
      // while still showing progression. This compresses the range non-linearly.
      const heatmapData = processedData.map(item => {
        let scaledWeight = item.weight;

        if (viewOption === "cumulative" && maxValue > 0) {
          // Apply square root scaling: sqrt(value/max) * max
          // This makes small values more visible while preserving relative differences
          const normalizedValue = item.weight / maxValue; // 0 to 1
          const scaledNormalized = Math.sqrt(normalizedValue); // Apply sqrt
          scaledWeight = scaledNormalized * maxValue; // Scale back
        }

        return {
          location: item.location,
          weight: scaledWeight,
        };
      });

      // For cumulative view with scaling, we need to adjust maxIntensity accordingly
      const maxIntensity = viewOption === "cumulative" && fixedMaxDensity > 0
        ? Math.sqrt(1) * fixedMaxDensity // sqrt(1) = 1, so this is just fixedMaxDensity
        : maxValue;

      hmap.setData(heatmapData);
      hmap.set("gradient", settings.type === "carbon" ? CO2Gradient : electrecityGradient);
      hmap.set("radius", calculateRadius());
      hmap.set("maxIntensity", maxIntensity);
    };

    const hideHeatmap = () => {
      if (heatMapLayer) {
        heatMapLayer.setMap(null);
        setHeatmapLayer(null);
      }
    };

    if (settings.heatmapOn) {
      showHeatmap();
    } else {
      hideHeatmap();
    }
  }, [map, processedData, settings, calculateRadius, heatMapLayer, viewOption, fixedMaxDensity, maxDensity]);


  useEffect(() => {
    if (!map) return;

    console.log('Legend useEffect triggered - viewOption:', viewOption, 'maxDensity:', maxDensity, 'processedData length:', processedData?.length, 'settings.type:', settings.type);

    // Add a small delay to ensure DOM elements are rendered
    const updateLegend = () => {
      const legendElement = document.getElementById('legend');
      if (!legendElement) return;

      const legendGradientElement = document.getElementById('legendGradient');
      if (!legendGradientElement) return;

      const legendWidth = legendGradientElement.offsetWidth;
    const numLabels = 4;
    
    // Calculate appropriate step size based on view type and max density
    let step;
    let maxValue;
    
    if (viewOption === "cumulative") {
      // For cumulative view, use the FIXED maxDensity (calculated for entire date range)
      if (fixedMaxDensity && fixedMaxDensity > 0) {
        maxValue = fixedMaxDensity;
        console.log('Cumulative view - using FIXED maxDensity for entire range:', maxValue);
      } else if (maxDensity && maxDensity > 0) {
        // Fallback to regular maxDensity if fixedMaxDensity not available
        maxValue = maxDensity;
        console.log('Cumulative view - using maxDensity prop (fallback):', maxValue);
      } else if (processedData && processedData.length > 0) {
        const weights = processedData.map(item => item.weight).filter(w => w > 0);
        console.log('Cumulative view - processedData length:', processedData.length);
        console.log('Cumulative view - weights:', weights);
        if (weights.length > 0) {
          maxValue = Math.max(...weights);
          console.log('Cumulative view - calculated maxValue from weights:', maxValue);
        } else {
          maxValue = 1;
          console.log('Cumulative view - no valid weights, using maxValue = 1');
        }
      } else {
        maxValue = 1;
        console.log('Cumulative view - no processedData, using maxValue = 1');
      }
    } else {
      // For daily view, use the maxDensity prop or fallback to processed data
      if (maxDensity && maxDensity > 0) {
        maxValue = maxDensity;
      } else if (processedData && processedData.length > 0) {
        const weights = processedData.map(item => item.weight).filter(w => w > 0);
        if (weights.length > 0) {
          maxValue = Math.max(...weights);
        } else {
          maxValue = 1;
        }
      } else {
        maxValue = 1;
      }
    }

    // Calculate step size
    step = Math.ceil(maxValue / (numLabels - 2));
    
    // Ensure maxValue and step are valid numbers
    if (!maxValue || isNaN(maxValue) || maxValue <= 0) {
      maxValue = 1;
    }
    if (!step || isNaN(step) || step <= 0) {
      step = 1;
    }

    console.log(`Legend update - View: ${viewOption}, Max Value: ${maxValue}, Step: ${step}`);

    // Clear existing labels
    Array.from(legendElement.children).forEach(child => {
      if (!child.id) {
        legendElement.removeChild(child);
      }
    });

    const addLabel = (left, top, width, height, background, text, fontSize = '0.8em') => {
      const div1 = document.createElement('div');
      div1.style.position = 'absolute';
      div1.style.left = left;
      div1.style.top = top;
      div1.style.width = width;
      div1.style.height = height;
      div1.style.background = background;
      legendElement.appendChild(div1);

      const div2 = document.createElement('div');
      div2.className = 'legend-label';
      div2.style.position = 'absolute';
      div2.style.left = (parseInt(left, 10) - 20) + 'px';
      div2.style.top = (parseInt(top, 10) + 20) + 'px';
      div2.style.width = '40px';
      div2.style.textAlign = 'center';
      div2.innerHTML = text;
      legendElement.appendChild(div2);
    };

    // Add legend labels - position them below the gradient
    const gradientTop = 30; // Position below title with proper spacing
    const labelTop = gradientTop + 20; // Position below the gradient bar
    
    addLabel('0px', gradientTop + 'px', '1px', '3px', 'black', '0');
    for (let i = step; i < maxValue; i += step) {
      let offset = i * legendWidth / maxValue;
      offset -= 0.5;
      addLabel(offset + 'px', gradientTop + 'px', '1px', '3px', 'black', i.toLocaleString());
    }
    addLabel((legendWidth - 1) + 'px', gradientTop + 'px', '1px', '3px', 'black', maxValue.toLocaleString());

    // Update gradient based on data type and position it correctly
    const gradient = settings.type === "carbon" ? CO2Gradient : electrecityGradient;
    let gradientCss = '(left';
    for (let i = 0; i < gradient.length; ++i) {
      gradientCss += ', ' + gradient[i];
    }
    gradientCss += ')';
    const gradientElement = document.getElementById('legendGradient');
    gradientElement.style.background = '-webkit-linear-gradient' + gradientCss;
    gradientElement.style.position = 'absolute';
    gradientElement.style.top = gradientTop + 'px';
    gradientElement.style.left = '0px';
    
    // Add view type indicator to legend
    const viewTypeIndicator = document.createElement('div');
    viewTypeIndicator.className = 'legend-title';
    
    // Create more informative legend title
    let legendTitle = '';
    if (viewOption === 'cumulative') {
      legendTitle = `Cumulative ${settings.type === 'carbon' ? 'CO₂ Emissions' : 'Electricity Usage'}`;
    } else {
      legendTitle = `Daily ${settings.type === 'carbon' ? 'CO₂ Emissions' : 'Electricity Usage'}`;
    }
    
    viewTypeIndicator.innerHTML = legendTitle;
    legendElement.appendChild(viewTypeIndicator);
    

    
    // Adjust legend container height to accommodate all content
    const totalHeight = gradientTop + 25; // gradient + labels + minimal padding
    legendElement.style.height = totalHeight + 'px';
    };

    // Use setTimeout to ensure DOM elements are available
    const timeoutId = setTimeout(updateLegend, 10);

    return () => clearTimeout(timeoutId);
  }, [map, maxDensity, fixedMaxDensity, settings.type, viewOption, processedData]);

  const onLoad = useCallback(function (mapInstance) {
    setMap(mapInstance);
    const hmap = new window.google.maps.visualization.HeatmapLayer({
      data: [],
      map: mapInstance,
      radius: calculateRadius(),
    });
    setHeatmapLayer(hmap);
    mapInstance.setZoom(3);
  }, [calculateRadius]);

  const onUnmount = useCallback(function () {
    setMap(null);
    setHeatmapLayer(null);
  }, []);



  const handleMarkerClick = useCallback((clickedLocation) => {
    const filterTypeKey = settings.type === "carbon" ? 'co2e_emissions' : 'electricity_usage';
    let total;
    let poolShares;

    if (viewOption === "cumulative") {
      const cumulativeData = data.reduce((acc, location) => {
        if (location.location_of_servers.location_name === clickedLocation.location_of_servers.location_name) {
          location.servers_at_location.forEach((server) => {
            acc.total += server[filterTypeKey];
            if (!acc.pools[server.blockchain_pool_name]) {
              acc.pools[server.blockchain_pool_name] = 0;
            }
            acc.pools[server.blockchain_pool_name] += server[filterTypeKey];
          });
        }
        return acc;
      }, { total: 0, pools: {} });

      total = cumulativeData.total;
      poolShares = Object.entries(cumulativeData.pools).map(([name, value]) => ({
        name: name || "Unavailable",
        share: (value * 100 / total).toFixed(2),
      }));

    } else {
      total = clickedLocation.servers_at_location.reduce((acc, curr) => acc + curr[filterTypeKey], 0);
      poolShares = clickedLocation.servers_at_location.map(pool => {
        return {
          name: pool.blockchain_pool_name || "Unavailable",
          share: (pool[filterTypeKey] * 100 / total).toFixed(2),
        };
      });
    }

    const formattedTotalUsage = total && !isNaN(total) ? (Math.round(total * 100) / 100).toLocaleString() : '0';

    setInfoWindow({
      locationName: clickedLocation.location_of_servers.location_name,
      totalUsage: formattedTotalUsage,
      content: poolShares,
      position: {
        lat: parseFloat(clickedLocation.location_of_servers.latitude),
        lng: parseFloat(clickedLocation.location_of_servers.longitude),
      },
    });
  }, [data, settings.type, viewOption]);

  // Only render map when Google Maps is loaded
  if (!isLoaded || !LatLngClass) {
    return (
      <div style={{ 
        width: "100%", 
        height: "100%", 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center",
        backgroundColor: "rgba(240, 248, 255, 0.3)",
        border: "1px solid #e0e0e0",
        borderRadius: "4px"
      }}>
        <div style={{
          textAlign: "center",
          padding: "20px"
        }}>
          <LoadingSpinner 
            variant="ripple" 
            size="medium" 
            text="Loading Google Maps..." 
            className="text-blue-600"
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <GoogleMap
          mapContainerStyle={containerStyle}
          clickableIcons={false}
          options={mapOptions}
          center={CENTER}
          zoom={3}
          onLoad={onLoad}
          onUnmount={onUnmount}
      >
        {settings.markersOn && processedData && processedData.map((item, i) => (
            <MarkerF
                key={i}
                position={item.location}
                onClick={() => handleMarkerClick(item.locationData)}
            />
        ))}

        {infoWindow && (
            <InfoWindow
                position={infoWindow.position}
                onCloseClick={() => setInfoWindow(null)}
            >
              <div style={{ paddingTop: "0px", marginTop: "0px" }}>
                <div
                    className="info-header"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      paddingBottom: "10px",
                      marginBottom: "10px",
                      borderBottom: "1px solid #ccc"
                    }}
                >
                  <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "10px",
                        fontSize: "1.2em",
                        fontWeight: "bold"
                      }}
                  >
                    <div style={{ marginRight: "20px" }}>
                      {infoWindow.locationName}
                    </div>
                    <div>
                      {infoWindow.totalUsage}
                      <span className="ml-1 font-medium">
                    {settings.type === "carbon" ? 'tCO₂e' : 'GWh'}
                  </span>
                    </div>
                  </div>
                </div>
                <table className="text-left mt-2" style={{ width: "100%" }}>
                  <thead>
                  <tr>
                    <th className="pr-2">Pool Name</th>
                    <th>% of Total</th>
                  </tr>
                  </thead>
                  <tbody>
                  {infoWindow.content.map((pool, index) => (
                      <tr key={index}>
                        <td className="pr-2">{pool.name}</td>
                        <td>{pool.share}%</td>
                      </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            </InfoWindow>
        )}
      </GoogleMap>
      
      {/* Only show SpinningNumber and Legend when not loading */}
      {!isLoading && (
        <>
          <div style={{ position: "absolute", top: "-50px", left: "50%", transform: "translateX(-50%)", zIndex: 50 }}>
            <SpinningNumber type={settings.type} totalCO2={totalCO2} totalElectricity={totalElectricity} fontSize="14px" />
          </div>
          
          {/* Legend */}
          <div id="legend">
            <div id="legendGradient"></div>
          </div>
        </>
      )}
    </div>
  );
}

export default React.memo(Map);
